import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle2, AlertTriangle, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const KIND_LABEL = {
  discipline: 'Discipline',
  figure: 'Figure',
  dance: 'Dance',
};

const EMPTY_RESOLUTION = { action: 'map', target_id: '', payload: {} };

function statusChip(status) {
  if (status === 'id_match') {
    return <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Matched by ID</span>;
  }
  if (status === 'fallback_match') {
    return <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Matched by name</span>;
  }
  if (status === 'ambiguous') {
    return <span className="inline-flex items-center gap-1 text-xs text-amber-700"><HelpCircle className="h-3 w-3" /> Needs choice (multiple matches)</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs text-red-700"><AlertTriangle className="h-3 w-3" /> Not found</span>;
}

function CreateFieldsFigure({ payload, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2 mt-1">
      <div>
        <Label className="text-xs">Number *</Label>
        <Input
          type="number"
          value={payload.number ?? ''}
          onChange={(e) => onChange({ ...payload, number: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-xs">Display #</Label>
        <Input
          value={payload.display_number ?? ''}
          onChange={(e) => onChange({ ...payload, display_number: e.target.value })}
        />
      </div>
      <div className="col-span-2">
        <Label className="text-xs">Description *</Label>
        <Input
          value={payload.description ?? ''}
          onChange={(e) => onChange({ ...payload, description: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-xs">Starting foot</Label>
        <select
          value={payload.default_starting_foot ?? payload.starting_foot ?? 'Either'}
          onChange={(e) => onChange({ ...payload, default_starting_foot: e.target.value })}
          className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option>Either</option>
          <option>Left</option>
          <option>Right</option>
        </select>
      </div>
      <div>
        <Label className="text-xs">Figure name</Label>
        <Input
          value={payload.figure_name ?? ''}
          onChange={(e) => onChange({ ...payload, figure_name: e.target.value })}
        />
      </div>
    </div>
  );
}

function CreateFieldsDance({ payload, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2 mt-1">
      <div className="col-span-2">
        <Label className="text-xs">Name *</Label>
        <Input
          value={payload.name ?? ''}
          onChange={(e) => onChange({ ...payload, name: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-xs">Tempo</Label>
        <Input
          value={payload.tempo ?? ''}
          onChange={(e) => onChange({ ...payload, tempo: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-xs">Sequences required</Label>
        <Input
          type="number"
          value={payload.sequences_required ?? ''}
          onChange={(e) => onChange({ ...payload, sequences_required: e.target.value })}
        />
      </div>
    </div>
  );
}

function CreateFieldsDiscipline({ payload, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2 mt-1">
      <div className="col-span-2">
        <Label className="text-xs">Name *</Label>
        <Input
          value={payload.discipline ?? payload.name ?? ''}
          onChange={(e) => onChange({ ...payload, discipline: e.target.value })}
        />
      </div>
      <div>
        <Label className="text-xs">Type</Label>
        <Input
          value={payload.type ?? payload.discipline_type ?? ''}
          onChange={(e) => onChange({ ...payload, type: e.target.value })}
          placeholder="e.g. Figures, Free Skating"
        />
      </div>
      <div>
        <Label className="text-xs">Competitor type</Label>
        <select
          value={payload.competitor_type ?? 'Individual'}
          onChange={(e) => onChange({ ...payload, competitor_type: e.target.value })}
          className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option>Individual</option>
          <option>Pairs</option>
          <option>Team</option>
        </select>
      </div>
      <div className="col-span-2 flex flex-wrap gap-x-4 gap-y-1 pt-1">
        {['two_round', 'rollart', 'local_discipline', 'own_music'].map((flag) => (
          <label key={flag} className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={!!payload[flag]}
              onCheckedChange={(v) => onChange({ ...payload, [flag]: !!v })}
            />
            {flag.replace('_', ' ')}
          </label>
        ))}
      </div>
    </div>
  );
}

function ResolutionCard({ ref: refItem, resolution, candidates, onChange }) {
  const action = resolution?.action || 'map';
  const payload = resolution?.payload || {};
  const targetId = resolution?.target_id || '';

  const setAction = (next) => onChange({ ...EMPTY_RESOLUTION, ...resolution, action: next, payload: next === 'create' ? { ...(refItem.workbook_fields || {}), ...payload } : {} });
  const setTarget = (id) => onChange({ ...resolution, action: 'map', target_id: id });
  const setPayload = (p) => onChange({ ...resolution, action: 'create', payload: p });

  return (
    <div className="border border-stone-200 rounded-md p-3 bg-stone-50/60" data-testid={`import-ref-${refItem.ref_key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-stone-800">
            {KIND_LABEL[refItem.kind]}: {refItem.workbook_display}
          </div>
          <div className="text-xs text-stone-500">
            Workbook ID <code>{refItem.workbook_id}</code>
          </div>
        </div>
        <div>{statusChip(refItem.status)}</div>
      </div>

      <div className="mt-3 space-y-2">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name={`action-${refItem.ref_key}`}
            value="map"
            checked={action === 'map'}
            onChange={() => setAction('map')}
          />
          <span className="flex-1">
            <span className="block">Map to existing {KIND_LABEL[refItem.kind].toLowerCase()}</span>
            {action === 'map' && (
              <select
                value={targetId}
                onChange={(e) => setTarget(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-input bg-white px-3 text-sm"
                data-testid={`import-map-${refItem.ref_key}`}
              >
                <option value="">Select…</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            )}
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name={`action-${refItem.ref_key}`}
            value="create"
            checked={action === 'create'}
            onChange={() => setAction('create')}
          />
          <span className="flex-1">
            <span className="block">Create a new {KIND_LABEL[refItem.kind].toLowerCase()}</span>
            {action === 'create' && (
              <>
                {refItem.kind === 'figure' && (
                  <CreateFieldsFigure payload={payload} onChange={setPayload} />
                )}
                {refItem.kind === 'dance' && (
                  <CreateFieldsDance payload={payload} onChange={setPayload} />
                )}
                {refItem.kind === 'discipline' && (
                  <CreateFieldsDiscipline payload={payload} onChange={setPayload} />
                )}
              </>
            )}
          </span>
        </label>
      </div>
    </div>
  );
}

export function ImportScheduleDialog({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('new');
  const [targetId, setTargetId] = useState('');
  const [emptySchedules, setEmptySchedules] = useState([]);
  const [busy, setBusy] = useState(false);

  const [sessionId, setSessionId] = useState('');
  const [summary, setSummary] = useState(null);
  const [refs, setRefs] = useState([]);
  const [resolutions, setResolutions] = useState({});
  const [pickers, setPickers] = useState({ disciplines: [], figures: [], dances: [] });

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setFile(null);
    setMode('new');
    setTargetId('');
    setSessionId('');
    setSummary(null);
    setRefs([]);
    setResolutions({});
    axios.get(`${API}/schedules/importable`, { withCredentials: true })
      .then((r) => setEmptySchedules(r.data || []))
      .catch(() => setEmptySchedules([]));
    axios.get(`${API}/content-library/pickers`, { withCredentials: true })
      .then((r) => setPickers(r.data || { disciplines: [], figures: [], dances: [] }))
      .catch(() => setPickers({ disciplines: [], figures: [], dances: [] }));
  }, [open]);

  const unresolved = useMemo(() => refs.filter((r) => r.status === 'ambiguous' || r.status === 'missing'), [refs]);

  const candidatesFor = (kind) => {
    if (kind === 'discipline') return pickers.disciplines;
    if (kind === 'figure') return pickers.figures;
    if (kind === 'dance') return pickers.dances;
    return [];
  };

  const submitPreview = async () => {
    if (!file) {
      toast.error('Please choose an .xlsx workbook');
      return;
    }
    if (mode === 'existing' && !targetId) {
      toast.error('Pick an empty schedule to import into');
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/schedules/import/preview`, form, { withCredentials: true });
      setSessionId(res.data.session_id);
      setSummary(res.data.summary);
      setRefs(res.data.references || []);
      // Seed default resolutions for ambiguous rows → map to the first match.
      const seeded = {};
      (res.data.references || []).forEach((ref) => {
        if (ref.status === 'ambiguous' && ref.fallback_matches?.length) {
          seeded[ref.ref_key] = { action: 'map', target_id: ref.fallback_matches[0], payload: {} };
        } else if (ref.status === 'missing') {
          seeded[ref.ref_key] = {
            action: 'create',
            target_id: '',
            payload: { ...(ref.workbook_fields || {}) },
          };
        }
      });
      setResolutions(seeded);
      // If nothing needs resolution, commit straight away.
      const needsResolution = (res.data.references || []).some((r) => r.status === 'ambiguous' || r.status === 'missing');
      if (!needsResolution) {
        await submitCommit(res.data.session_id, {});
      } else {
        setStep(2);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const submitCommit = async (overrideSessionId, overrideResolutions) => {
    const sid = overrideSessionId || sessionId;
    const resos = overrideResolutions === undefined ? resolutions : overrideResolutions;
    // Validate — every unresolved ref must have a complete resolution.
    for (const r of refs) {
      if (r.status !== 'ambiguous' && r.status !== 'missing') continue;
      const chosen = resos[r.ref_key];
      if (!chosen) return toast.error(`Resolve "${r.workbook_display}" first`);
      if (chosen.action === 'map' && !chosen.target_id) {
        return toast.error(`Pick a target for "${r.workbook_display}" first`);
      }
      if (chosen.action === 'create') {
        if (r.kind === 'discipline' && !(chosen.payload?.discipline || chosen.payload?.name)) {
          return toast.error(`Enter a name for new discipline "${r.workbook_display}"`);
        }
        if (r.kind === 'figure') {
          if (!chosen.payload?.description) return toast.error(`Enter a description for new figure "${r.workbook_display}"`);
          if (chosen.payload?.number === undefined || chosen.payload?.number === '') {
            return toast.error(`Enter a number for new figure "${r.workbook_display}"`);
          }
        }
        if (r.kind === 'dance' && !chosen.payload?.name) {
          return toast.error(`Enter a name for new dance "${r.workbook_display}"`);
        }
      }
    }

    setBusy(true);
    try {
      const body = {
        session_id: sid,
        target_schedule_id: mode === 'existing' ? targetId : null,
        resolutions: resos,
      };
      const res = await axios.post(`${API}/schedules/import/commit`, body, { withCredentials: true });
      toast.success(
        `Imported ${res.data.grades_imported} grades into ${res.data.schedule_type || 'schedule'}`,
      );
      onImported?.();
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.unresolved)) {
        toast.error(detail.message || 'Some references still need a resolution');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Import failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl" data-testid="import-schedule-dialog">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? 'Import New Schedule' : 'Resolve References'}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Load a Schedule previously exported from CompCreator. Matching cascades:
              Discipline by ID → name; Figure by ID → (number + description); Dance by
              ID → name. Anything that still can&apos;t be matched will be surfaced for
              you to map or create on the next step.
            </p>
            <div>
              <Label className="text-sm">Workbook (.xlsx) *</Label>
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm"
                data-testid="import-schedule-file"
                disabled={busy}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Target</Label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="import-mode"
                  value="new"
                  checked={mode === 'new'}
                  onChange={() => setMode('new')}
                  disabled={busy}
                  data-testid="import-mode-new"
                />
                <span>Create a new Schedule using the workbook&apos;s Schedule sheet.</span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="import-mode"
                  value="existing"
                  checked={mode === 'existing'}
                  onChange={() => setMode('existing')}
                  disabled={busy}
                  data-testid="import-mode-existing"
                />
                <span>Import into an existing empty Schedule.</span>
              </label>
              {mode === 'existing' && (
                <select
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                  data-testid="import-target-select"
                  disabled={busy}
                >
                  <option value="">Select an empty schedule…</option>
                  {emptySchedules.map((s) => (
                    <option key={s.schedule_id} value={s.schedule_id}>
                      {s.schedule_type}{s.schedule_description ? ` — ${s.schedule_description}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button
                className="btn-primary"
                onClick={submitPreview}
                disabled={busy || !file}
                data-testid="import-schedule-preview"
              >
                {busy ? 'Checking…' : 'Check References'}
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {summary && (
              <div className="text-sm bg-emerald-50 border border-emerald-200 rounded p-3">
                <div className="font-semibold text-emerald-900">
                  {summary.auto_resolved} of {summary.total_references} references matched automatically
                </div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  {summary.total_grades} grades · {summary.total_groups} figure groups · {summary.total_compulsories} compulsories
                </div>
              </div>
            )}

            {unresolved.length === 0 ? (
              <p className="text-sm">Nothing left to resolve — ready to import.</p>
            ) : (
              <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                {unresolved.map((ref) => (
                  <ResolutionCard
                    key={ref.ref_key}
                    ref={ref}
                    resolution={resolutions[ref.ref_key]}
                    candidates={candidatesFor(ref.kind)}
                    onChange={(next) => setResolutions((prev) => ({ ...prev, [ref.ref_key]: next }))}
                  />
                ))}
              </div>
            )}

            <div className="flex justify-between items-center gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(1)} disabled={busy}>Back</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                <Button
                  className="btn-primary"
                  onClick={() => submitCommit()}
                  disabled={busy}
                  data-testid="import-schedule-commit"
                >
                  {busy ? 'Importing…' : 'Import Schedule'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
